import { onMounted, onUnmounted, ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { TokenDto } from "@/api/generated";
import WebSocketClient from "@/libs/WebSocketClient";

export interface ILiveTokenData {
  token: TokenDto;
  onUpdate?: (token: TokenDto) => void;
}

/**
 * Custom composable function that provides live token data for a given token.
 * @param token - The token object.
 * @returns An object containing the reactive token data.
 */
export function useLiveTokenData({ token, onUpdate }: ILiveTokenData) {
  const tokenData = ref<TokenDto>({
    ...token,
  });

  const subscription = ref();
  const queryClient = useQueryClient();

  onMounted(() => {
    subscription.value = WebSocketClient.subscribeForTokenUpdates(
      token.sale_address,
      (newComingTokenData: any) => {
        const newTokenData = {
          ...tokenData.value,
          ...newComingTokenData.data,
        };

        queryClient.setQueryData(
          ["TokensService.findByAddress", token.sale_address],
          newTokenData
        );
        queryClient.setQueryData(
          ["TokensService.findByAddress", token.symbol],
          newTokenData
        );

        queryClient.refetchQueries({
          queryKey: ["TokensService.listTokenRankings", token.sale_address],
        });

        queryClient.refetchQueries({
          queryKey: ["TokensService.getHolders", token.sale_address],
        });

        tokenData.value = newTokenData;
        onUpdate?.(newTokenData);
      }
    );
  });

  onUnmounted(() => {
    subscription?.value?.();
  });

  return {
    tokenData,
  };
}
